import { Button, toast } from "@kkp/ui";
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../../../ui/Dialog";
import { Field, Input } from "../../../ui/Input";

type EditCompanyFormApi = ReturnType<
  typeof import("../hooks/useEditCompanyForm").useEditCompanyForm
>;

export function EditCompanyDialog({ form }: { form: EditCompanyFormApi }) {
  const {
    isOpen,
    setIsOpen,
    editingCompany,
    register,
    errors,
    isSubmitting,
    onSubmit,
    cancel,
  } = form;

  return (
    <Dialog open={isOpen} onOpenChange={setIsOpen}>
      <DialogContent wide className="kkp-flowbite">
        <DialogHeader className="flex flex-col gap-1.5 sm:flex-row sm:items-start sm:justify-between">
          <div className="grid gap-1">
            <DialogTitle>Edit Company</DialogTitle>
            <DialogDescription>
              Update company details or reset the PIC password.
            </DialogDescription>
          </div>
        </DialogHeader>

        <form
          className="grid gap-5 rounded-[var(--kkp-radius)] border border-[color:rgba(255,255,255,0.08)] bg-[color:rgba(24,19,36,0.35)] p-4 sm:p-5"
          onSubmit={onSubmit}
        >
          <div className="flex flex-wrap items-center gap-2">
            <Button
              size="sm"
              variant="secondary"
              type="button"
              onClick={() =>
                toast.info("PIC password reset requested.", {
                  title: editingCompany?.name ?? "Company",
                  durationMs: 2000,
                })
              }
            >
              Reset PIC Password
            </Button>
          </div>

          <div className="grid gap-4 sm:grid-cols-2">
            <Field label="Company legal name" error={errors.legalName?.message}>
              <Input
                placeholder="Company legal name"
                {...register("legalName")}
              />
            </Field>

            <Field label="Back-office ID" error={errors.backOfficeId?.message}>
              <Input placeholder="KKP/2605/001" {...register("backOfficeId")} />
            </Field>

            <Field label="Tax ID (NPWP)" error={errors.taxId?.message}>
              <Input placeholder="NPWP" {...register("taxId")} />
            </Field>

            <Field
              label="Legal address"
              error={errors.legalAddress?.message}
              className="sm:col-span-2"
            >
              <Input
                placeholder="Jl. Sudirman No. 1, Jakarta"
                {...register("legalAddress")}
              />
            </Field>

            <Field
              label="Fee per transaction (IDR)"
              error={errors.perTransactionFeeIdr?.message}
            >
              <Input
                placeholder="2500"
                inputMode="numeric"
                {...register("perTransactionFeeIdr")}
              />
            </Field>

            <Field
              label="Source bank code"
              error={errors.sourceBankCode?.message}
            >
              <Input placeholder="002" {...register("sourceBankCode")} />
            </Field>

            <Field
              label="Source bank account"
              error={errors.sourceBankAccount?.message}
            >
              <Input
                placeholder="1234567890"
                {...register("sourceBankAccount")}
              />
            </Field>

            <Field label="PIC fullname" error={errors.picFullname?.message}>
              <Input placeholder="PIC fullname" {...register("picFullname")} />
            </Field>

            <Field
              label="PIC phonenumber"
              error={errors.picPhonenumber?.message}
            >
              <Input
                placeholder="+62 812 0000 1234"
                inputMode="tel"
                {...register("picPhonenumber")}
              />
            </Field>

            <Field label="PIC email">
              <Input
                readOnly
                disabled
                placeholder="Sign-in identity, cannot be changed"
              />
            </Field>
          </div>

          <DialogFooter>
            <DialogClose asChild>
              <Button variant="ghost" type="button" onClick={cancel}>
                Cancel
              </Button>
            </DialogClose>
            <Button variant="primary" type="submit" disabled={isSubmitting}>
              {isSubmitting ? "Saving…" : "Save changes"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
